import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
  Button,
  Card,
  CardBody,
  Select,
  SelectItem,
  Input,
} from "@heroui/react";
import { AlertInfo, Play, Plus } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import ModalViewContract from "./modalViewContract";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { BuildingDTO } from "shared/types/building";
import { useSession } from "@clerk/clerk-react";
import {
  ContactType,
  ContractContact,
  ContractMilestoneDate,
  ContractPhase,
  ContractProduct,
  MilestoneDateType,
  RentalContract,
} from "shared/types/rental-contracts";
import { applyMask } from "shared/utils/masks";
import { findActivePrice } from "shared/utils/priceHistory";
import ColoredDot from "../operationalDashboard/components/coloredDot";
import { UnitDTO } from "shared/types/unit";
import { formatDate } from "shared/utils/get-dates";
import { FileLabel } from "shared/types/file";
import {
  Archive,
  CloudSlash,
  Eye,
  FileDashed,
  ListBullets,
  User,
  Trash,
  Globe,
  LinkSimple,
  CaretDown,
  CaretUp,
  UsersThree,
  FileText,
} from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { ActivateContractModal } from "./modals/activateContract";
import DocumentGeneratorComponent from "components/documents/documentGeneratorComponent";
import { DocumentCategory } from "components/documents/documentGeneratorModal";
import { TerminateContractModal } from "./modals/terminateContract";
import {
  FlagForWatchListModal,
  WatchListModalState,
} from "./modals/flagForWatchlist";
import { TerminateContractAbruptModal } from "./modals/terminateContractAbrupt";
import { useLocation, useNavigate } from "react-router-dom";
import { ActiveContractsTable } from "./activeContractsTable";
import { OdooContractAction } from "./components/OdooContractAction";
import { ArchivedContractsTable } from "./archivedContractsTable";
import { RenderPrice } from "./renderPrice";
import { CandidateList } from "components/candidates/CandidateList";
import { SetFuturePriceModal } from "./modals/setFuturePrice";
import ModalEditContractProducts from "./modalEditContractProducts";

interface RentalTabProps {
  building: BuildingDTO;
  refreshBuilding?: () => void;
}

const requiredFileLabels = [
  FileLabel.Huurcontract,
  FileLabel.IngaandePlaatsbeschrijving,
  FileLabel.BrandverzekeringHuurder,
  FileLabel.RegistratiebewijsHuurcontract,
];

export const RentalTab = ({ building, refreshBuilding }: RentalTabProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rentalContracts, setRentalContracts] = useState<RentalContract[]>([]);
  const [isViewContractModalOpen, setIsViewContractModalOpen] = useState(false);

  const [selectedContract, setSelectedContract] =
    useState<RentalContract | null>(null);
  const [comparingOdoo, setComparingOdoo] = useState(false);
  const [isActivateContractModalOpen, setIsActivateContractModalOpen] =
    useState(false);
  const [isTerminateContractModalOpen, setIsTerminateContractModalOpen] =
    useState(false);
  const [
    isTerminateContractAbruptModalOpen,
    setIsTerminateContractAbruptModalOpen,
  ] = useState(false);
  const [isFlagForWatchListModalOpen, setIsFlagForWatchListModalOpen] =
    useState(false);
  const { session } = useSession();
  const [isPriceSyncModalOpen, setIsPriceSyncModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<ContractProduct | null>(
    null,
  );
  const [isSetFuturePriceModalOpen, setIsSetFuturePriceModalOpen] =
    useState(false);
  const [selectedProductForFuturePrice, setSelectedProductForFuturePrice] =
    useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contractToDelete, setContractToDelete] =
    useState<RentalContract | null>(null);
  const [isEditProductsModalOpen, setIsEditProductsModalOpen] = useState(false);
  const [selectedContractForProductEdit, setSelectedContractForProductEdit] = useState<RentalContract | null>(null);

  // Archived contracts state
  const [showArchivedContracts, setShowArchivedContracts] = useState(false);
  const [archivedContracts, setArchivedContracts] = useState<RentalContract[]>(
    [],
  );
  const [loadingArchivedContracts, setLoadingArchivedContracts] =
    useState(false);

  // Immoweb reference management
  const [selectedUnitForReference, setSelectedUnitForReference] =
    useState<string>("");
  const [immowebReferenceId, setImmowebReferenceId] = useState<string>("");
  const [isSavingReference, setIsSavingReference] = useState(false);

  // Collapsible sections
  const [isCandidatesCollapsed, setIsCandidatesCollapsed] = useState(true);
  const [isImmowebReferencesCollapsed, setIsImmowebReferencesCollapsed] =
    useState(true);

  // Refs for optimization
  const lastFetchTime = useRef<number>(0);
  const isFetching = useRef<boolean>(false);

  // Memoized categorized contracts (excluding closed contracts from main fetch)
  const { activeContracts, conceptContracts } = useMemo(() => {
    const active = rentalContracts.filter(
      (contract) =>
        contract.phase === ContractPhase.ACTIVE ||
        contract.phase === ContractPhase.TERMINATION_PHASE,
    );
    const concept = rentalContracts.filter(
      (contract) => contract.phase === ContractPhase.CONCEPT,
    );

    return { activeContracts: active, conceptContracts: concept };
  }, [rentalContracts]);

  const handleOpenPriceSyncModal = useCallback((product: any) => {
    if (!product) return toast.error("Kan niet synchroniseren op dit moment");
    console.log("Product:", product);
    setCurrentProduct(product);
    setIsPriceSyncModalOpen(true);
  }, []);

  const handleClosePriceSyncModal = useCallback(() => {
    setIsPriceSyncModalOpen(false);
  }, []);

  // Move evaluateContract to before fetchRentalContracts
  const evaluateContract = useCallback((contract: any) => {
    console.log("Evaluating contract:", {
      contractId: contract.id,
      documentStatuses: contract.documentStatuses,
      fileLogs: contract.fileLogs,
    });

    const operationalChecks: any = {
      green: { count: 0, labels: [] },
      orange: { count: 0, labels: [] },
      red: { count: 0, labels: [] },
      transparent: { count: 0, labels: [] },
    };

    // Check guarantee
    const hasGuarantee = contract.guarantee?.length > 0;
    if (hasGuarantee) {
      operationalChecks.green.count++;
      operationalChecks.green.labels.push("Huurgarantie aanwezig");
    } else {
      // Check if guarantee has a status
      const guaranteeStatus = contract.documentStatuses?.find(
        (status: any) => status.documentType === "Huurwaarborg",
      );
      if (guaranteeStatus) {
        if (guaranteeStatus.status === "MISSING_NOT_REQUIRED") {
          operationalChecks.green.count++;
          operationalChecks.green.labels.push(
            `Huurwaarborg niet vereist: ${guaranteeStatus.reason}`,
          );
        } else {
          operationalChecks.red.count++;
          operationalChecks.red.labels.push(
            `Huurwaarborg vereist maar niet beschikbaar: ${guaranteeStatus.reason}`,
          );
        }
      } else {
        operationalChecks.red.count++;
        operationalChecks.red.labels.push("Huurgarantie niet aanwezig");
      }
    }

    // Check required documents
    requiredFileLabels.forEach((label) => {
      console.log(`Checking document ${label} for contract ${contract.id}:`, {
        documentExists: contract.fileLogs?.some(
          (file: any) => file && file.label?.includes(label),
        ),
        documentStatus: contract.documentStatuses?.find(
          (status: any) => status.documentType === label,
        ),
      });

      const documentExists = contract.fileLogs?.some(
        (file: any) => file && file.label?.includes(label),
      );
      const documentStatus = contract.documentStatuses?.find(
        (status: any) => status.documentType === label,
      );

      if (documentExists) {
        operationalChecks.green.count++;
        operationalChecks.green.labels.push(`${label} aanwezig`);
      } else if (documentStatus) {
        if (documentStatus.status === "MISSING_NOT_REQUIRED") {
          operationalChecks.green.count++;
          operationalChecks.green.labels.push(
            `${label} niet vereist: ${documentStatus.reason}`,
          );
        } else {
          operationalChecks.red.count++;
          operationalChecks.red.labels.push(
            `${label} vereist maar niet beschikbaar: ${documentStatus.reason}`,
          );
        }
      } else {
        operationalChecks.red.count++;
        operationalChecks.red.labels.push(`${label} niet aanwezig`);
      }
    });

    console.log(
      `Final evaluation for contract ${contract.id}:`,
      operationalChecks,
    );
    return operationalChecks;
  }, []);

  const compareWithOdoo = useCallback(async (contractIds: number[]) => {
    try {
      setComparingOdoo(true);
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/compare-odoo`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(contractIds),
        },
        session,
      );
      const comparedContracts = await response.json();
      setRentalContracts((prevContracts) =>
        prevContracts.map((contract) => {
          const comparedContract = comparedContracts.find(
            (c: any) => c.id === contract.id,
          );
          if (comparedContract) {
            return {
              ...contract,
              activePrice: comparedContract.activePrice,
              activeProvision: comparedContract.activeProvision,
              activePropertyTaxes: comparedContract.activePropertyTaxes,
              buildingAttributes: comparedContract.buildingAttributes, // Include building attributes
            };
          }
          return contract;
        }),
      );
    } catch (error) {
      console.error("Error comparing contracts with Odoo:", error);
    } finally {
      setComparingOdoo(false);
    }
  }, []);

  const fetchRentalContracts = useCallback(
    async (force = false) => {
      // Prevent duplicate calls and unnecessary refreshes
      const now = Date.now();
      if (!force && isFetching.current) {
        console.log("Already fetching, skipping...");
        return;
      }

      if (!force && now - lastFetchTime.current < 15000) {
        // 15 second cache for faster updates
        console.log("Using cached data...");
        return;
      }

      try {
        isFetching.current = true;
        setLoading(true);

        const res = await fetchWithAuth(
          `${API_URL}/rental/contracts/building/${building.id}?excludeClosed=true`,
          { method: "GET" },
          session,
        );

        const data = await res.json();
        const sortedData = data.sort((a: any, b: any) =>
          // Attribute-only contracts (e.g. standalone parking) have no unit; sort them last.
          (a.unit?.id || "￿").localeCompare(b.unit?.id || "￿"),
        );

        const evaluatedContracts = sortedData.map(
          (contract: RentalContract) => ({
            ...contract,
            operationalChecks: evaluateContract(contract),
          }),
        );

        // PHASE 1: Show contracts immediately (fast loading)
        setRentalContracts(evaluatedContracts);
        setLoading(false); // Stop main loading, show contracts

        // PHASE 2: Load prices in background (slower)
        if (evaluatedContracts.length > 0) {
          // Use setTimeout to ensure UI updates before starting price comparison
          setTimeout(() => {
            compareWithOdoo(
              evaluatedContracts.map((contract: any) => contract.id),
            );
          }, 100);
        }

        lastFetchTime.current = now;
      } catch (error) {
        console.error("Error fetching rental contracts:", error);
        toast.error("Fout bij ophalen van contracten");
        setLoading(false);
      } finally {
        isFetching.current = false;
      }
    },
    [building.id, evaluateContract, compareWithOdoo],
  );

  const fetchArchivedContracts = useCallback(async () => {
    try {
      setLoadingArchivedContracts(true);

      const res = await fetchWithAuth(
        `${API_URL}/rental/contracts/building/${building.id}?onlyClosed=true`,
        { method: "GET" },
        session,
      );

      const data = await res.json();
      const sortedData = data.sort((a: any, b: any) =>
        a.unit.id.localeCompare(b.unit.id),
      );

      const evaluatedContracts = sortedData.map((contract: RentalContract) => ({
        ...contract,
        operationalChecks: evaluateContract(contract),
      }));

      setArchivedContracts(evaluatedContracts);
    } catch (error) {
      console.error("Error fetching archived contracts:", error);
      toast.error("Fout bij ophalen van gearchiveerde contracten");
    } finally {
      setLoadingArchivedContracts(false);
    }
  }, [building.id, evaluateContract, session]);

  // Wrapper for compatibility with existing modal components
  const refreshRentalContracts = useCallback(() => {
    // Reset price loading state when refreshing
    fetchRentalContracts(true);
  }, [fetchRentalContracts]);

  // Wrapper for modal components that expect a function with no parameters
  const handleContractRefresh = useCallback(
    (id?: number) => {
      if (id) {
        // If an ID is provided, only refresh that specific contract
        fetchRentalContracts(true);
      } else {
        // Otherwise refresh all contracts
        fetchRentalContracts(true);
      }
    },
    [fetchRentalContracts],
  );

  // Page visibility API to optimize refreshes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && Date.now() - lastFetchTime.current > 60000) {
        fetchRentalContracts(true); // Force refresh when becoming visible after 1 minute
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchRentalContracts]);

  useEffect(() => {
    fetchRentalContracts();
  }, [building.id, fetchRentalContracts]);

  const handleOpenViewContractModal = useCallback((contract: any) => {
    setSelectedContract(contract);
    setIsViewContractModalOpen(true);
  }, []);

  const handleCloseViewContractModal = useCallback(() => {
    setIsViewContractModalOpen(false);
    setSelectedContract(null);
  }, []);
  
  // Open the contract creation split-view page, passing along any prefilled data.
  // (Called directly by the toolbar button — where the first arg is a click event,
  // so we only treat it as contacts when it's actually an array.)
  const handleOpenAddContractModal = useCallback((
    contacts?: Array<{ id: number; name: string; isOnContract: boolean; resourceEntityId?: number; wasExisting?: boolean; residentData?: { email?: string | null; phoneNumber?: string | null; spokenLanguages?: string[]; nationality?: string | null; dateOfBirth?: string | null; placeOfBirth?: string | null; relationToApplicant?: string | null } }>,
    unitId?: string,
    contractType?: string,
    numResidents?: number
  ) => {
    navigate(`/building/${building.id}/verhuur/nieuw-contract`, {
      state: {
        company: building.company,
        units: building.units,
        building,
        prefilledContacts: Array.isArray(contacts) ? contacts : undefined,
        prefilledUnitId: unitId,
        prefilledContractType: contractType,
        prefilledNumResidents: numResidents,
      },
    });
  }, [navigate, building]);

  const handleFlagForWatchList = async (contract: any) => {
    setSelectedContract(contract);
    setIsFlagForWatchListModalOpen(true);
  };
  const handleCloseFlagForWatchListModal = () => {
    setSelectedContract(null);
    setIsFlagForWatchListModalOpen(false);
    fetchRentalContracts();
  };
  const handleCloseActivateContractModal = () => {
    setSelectedContract(null);
    setIsActivateContractModalOpen(false);
  };
  const handleCloseTerminateContractModal = () => {
    setSelectedContract(null);
    setIsTerminateContractModalOpen(false);
  };
  const handleCloseTerminateContractAbruptModal = () => {
    setSelectedContract(null);
    setIsTerminateContractAbruptModalOpen(false);
    fetchRentalContracts();
  };
  const handleActivateContract = async (contract: any) => {
    setSelectedContract(contract);
    setIsActivateContractModalOpen(true);
  };
  const handleTerminateContract = async (contract: any) => {
    setSelectedContract(contract);
    setIsTerminateContractModalOpen(true);
  };

  const handleTerminateContractAbruptly = async (contract: any) => {
    setSelectedContract(contract);
    setIsTerminateContractAbruptModalOpen(true);
  };

  const handleOpenEditProductsModal = (contract: RentalContract) => {
    setSelectedContractForProductEdit(contract);
    setIsEditProductsModalOpen(true);
  };

  const handleCloseEditProductsModal = () => {
    setSelectedContractForProductEdit(null);
    setIsEditProductsModalOpen(false);
  };

  const handleToggleArchivedContracts = () => {
    if (!showArchivedContracts) {
      // Show archived contracts and fetch them if not already loaded
      setShowArchivedContracts(true);
      if (archivedContracts.length === 0) {
        fetchArchivedContracts();
      }
    } else {
      // Hide archived contracts
      setShowArchivedContracts(false);
    }
  };

  const syncPricesFromOdoo = async () => {
    if (!currentProduct)
      return toast.error("Kan niet synchroniseren op dit moment");

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/sync-odoo-pull`,
        {
          method: "POST",
          body: JSON.stringify({ product: currentProduct }),
          headers: { "Content-Type": "application/json" },
        },
        session,
      );
      if (response.ok) {
        toast.success("Prices successfully synced from Odoo!");
        fetchRentalContracts();
        handleClosePriceSyncModal();
      } else {
        toast.error("Failed to sync prices from Odoo.");
      }
    } catch (error) {
      toast.error("Network or server error.");
    }
  };

  const calculateBuildingAttributeRevenue = (contract: RentalContract) => {
    console.log(
      `💰 RentalTab: calculateBuildingAttributeRevenue called for contract ${contract.id}`,
    );
    console.log(`💰 RentalTab: Contract products:`, contract.products);

    if (!contract.products) {
      console.log(
        `💰 RentalTab: No products found for contract ${contract.id}`,
      );
      return { total: 0, attributes: [] };
    }

    // Filter products that are building attributes (either by relation or by name pattern)
    const attributeProducts = contract.products.filter((product) => {
      // Check if it has a buildingAttribute relation
      if (product.buildingAttribute) {
        console.log(
          `💰 RentalTab: Product ${product.id} has buildingAttribute relation:`,
          product.buildingAttribute,
        );
        return true;
      }

      // Check if it looks like a building attribute by name
      const productNameLower = product.name.toLowerCase();
      const isAttributeByName =
        productNameLower.includes("attribuut") ||
        productNameLower.includes("buitenstaanplaats") ||
        productNameLower.includes("parkeerplaats") ||
        productNameLower.includes("garage") ||
        productNameLower.includes("berging") ||
        productNameLower.includes("kelder") ||
        productNameLower.includes("zolder") ||
        productNameLower.includes("tuin") ||
        productNameLower.includes("terras");

      if (isAttributeByName) {
        console.log(
          `💰 RentalTab: Product ${product.id} detected as building attribute by name: "${product.name}"`,
        );
        return true;
      }

      return false;
    });
    console.log(
      `💰 RentalTab: Found ${attributeProducts.length} building attribute products`,
    );

    const total = attributeProducts.reduce((sum, product) => {
      console.log(
        `💰 RentalTab: Processing product ${product.id} with ${product.price?.length || 0} prices`,
      );

      // Gedeelde regel (shared/utils/priceHistory): de laatst geregistreerde onder
      // de prijzen waarvan de ingangsdatum bereikt is. De vorige sortering viel bij
      // twee prijzen met DEZELFDE ingangsdatum terug op 0 en liet de volgorde dus
      // aan het toeval, waardoor het totaal een oudere prijs kon meenemen.
      const currentPrice = findActivePrice(product.price)?.amount ?? 0;

      return sum + currentPrice;
    }, 0);

    const attributes = attributeProducts
      .filter((product) => product.price && product.price.length > 0) // Filter out products without prices
      .map((product) => {
        // Gedeelde regel (shared/utils/priceHistory). Belangrijk: `priceId`
        // hieronder gaat mee naar de Odoo-sync, dus een verkeerde keuze hier duwt
        // ook een verkeerd bedrag naar de boekhouding. De vorige sortering gaf bij
        // twee prijzen met dezelfde ingangsdatum 0 terug en liet de volgorde dus
        // aan het toeval.
        const activePriceRecord = findActivePrice(product.price);
        const currentPrice = activePriceRecord?.amount ?? 0;

        return {
          id: product.id, // Backend expects "id" for sync
          name:
            product.buildingAttribute?.name ||
            product.name ||
            "Onbekend attribuut", // Display name (prefer attribute name)
          price: currentPrice,
          priceId: activePriceRecord?.id || null,
          productId: product.id,
          odooOrderLineId: product.odooOrderLineId, // Extract from product for sync
          // Include additional info for better display in tooltip
          isLinkedAttribute: !!product.buildingAttribute,
          productName: product.name, // Keep product name for reference
        };
      });

    console.log(
      `💰 RentalTab: Total building attribute revenue for contract ${contract.id}: €${total}`,
    );
    console.log(`💰 RentalTab: Attributes breakdown:`, attributes);

    return { total, attributes };
  };

  const renderBuildingAttributeRevenue = (contract: RentalContract) => {
    console.log(
      `🎨 RentalTab: renderBuildingAttributeRevenue called for contract ${contract.id}`,
    );

    // Use backend-provided building attributes if available (includes equalsInOdoo)
    const backendAttributes = (contract as any).buildingAttributes || [];
    console.log(
      `📊 Backend attributes:`,
      JSON.stringify(backendAttributes, null, 2),
    );

    // Fallback to local extraction if backend doesn't provide them (e.g., before comparison or products have no prices)
    let attributes: any[];
    let total: number;

    if (backendAttributes.length > 0) {
      // Use backend data (has equalsInOdoo for sync icons)
      console.log(
        `✅ Using BACKEND data (${backendAttributes.length} attributes)`,
      );
      attributes = backendAttributes;
      total = backendAttributes.reduce(
        (sum: number, attr: any) => sum + attr.price,
        0,
      );
    } else {
      console.log(`⚠️ Using LOCAL FALLBACK (no backend data)`);
      // Fallback to local extraction
      const localData = calculateBuildingAttributeRevenue(contract);
      console.log(
        `📊 Local data:`,
        JSON.stringify(localData.attributes, null, 2),
      );
      // Add equalsInOdoo: null so sync icons don't appear (we haven't compared)
      attributes = localData.attributes.map((attr: any) => ({
        ...attr,
        equalsInOdoo: null, // Prevent loading spinner - we haven't compared yet
        odooPrice: null, // Don't have Odoo's price until comparison
        // Keep odooOrderLineId, productId, priceId from local calculation
      }));
      total = localData.total;
    }

    console.log(
      `📊 FINAL attributes passed to RenderPrice:`,
      JSON.stringify(attributes, null, 2),
    );
    console.log(
      `🎨 RentalTab: Rendering total: €${total}, attributes: ${attributes.length}`,
    );

    if (total === 0 || attributes.length === 0) {
      console.log(
        `🎨 RentalTab: No revenue to render for contract ${contract.id}`,
      );
      return <span className="text-neutral-secondary-mid text-sm">-</span>;
    }

    // Check if this is a concept contract
    const isConceptContract = contract.phase === "In concept";
    const isActiveContract = contract.phase === "Lopende contracten";
    const isArchived = contract.phase === ContractPhase.CLOSED;

    const tooltipContent = (
      <div className="space-y-1">
        <div className="text-sm font-medium">Gebouw attributen:</div>
        {attributes.map((attr: any, index: number) => (
          <div key={index} className="space-y-1 text-xs">
            <div className="font-medium">{attr.name}</div>
            {attr.isLinkedAttribute &&
              attr.productName &&
              attr.productName !== attr.name && (
                <div className="text-xs opacity-75">
                  Product: {attr.productName}
                </div>
              )}
            <div className="text-right">
              Prijs: {applyMask(attr.price, "valuta_specific")}
            </div>
          </div>
        ))}
        <div className="mt-1 border-t border-white/20 pt-1 text-xs font-medium">
          Totaal: {applyMask(total, "valuta_specific")}
        </div>
      </div>
    );

    console.log(
      `🎨 RentalTab: Rendered revenue element for contract ${contract.id}`,
    );

    // If there are multiple attributes, show them all with sync icons
    if (attributes.length > 1) {
      return (
        <div className="flex flex-col gap-1">
          {attributes.map((attr: any, index: number) => (
            <div key={index} className="flex items-center gap-1.5 text-sm">
              <span className="text-neutral-secondary text-xs">
                {attr.name}:
              </span>
              <RenderPrice
                price={{
                  id: attr.productId, // Backend expects "id" for product lookup
                  name: attr.name, // Product name for display
                  price: attr.price,
                  equalsInOdoo: attr.equalsInOdoo,
                  odooOrderLineId: attr.odooOrderLineId,
                  odooPrice: attr.odooPrice,
                  productId: attr.productId, // Keep for debug tooltip
                  priceId: attr.priceId,
                }}
                label={attr.name}
                contract={contract}
                rentalInfo={rentalInfo}
                isArchived={isArchived}
              />
            </div>
          ))}
        </div>
      );
    }

    // Single attribute - show with label and sync icon
    if (attributes.length === 1) {
      const attr = attributes[0];
      return (
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-neutral-secondary text-xs">{attr.name}:</span>
          <RenderPrice
            price={{
              id: attr.productId, // Backend expects "id" for product lookup
              name: attr.name, // Product name for display
              price: attr.price,
              equalsInOdoo: attr.equalsInOdoo,
              odooOrderLineId: attr.odooOrderLineId,
              odooPrice: attr.odooPrice,
              productId: attr.productId, // Keep for debug tooltip
              priceId: attr.priceId,
            }}
            label={attr.name}
            contract={contract}
            rentalInfo={rentalInfo}
            isArchived={isArchived}
          />
        </div>
      );
    }

    return (
      <Tooltip content={tooltipContent}>
        <div className="flex cursor-pointer items-center gap-1">
          <span
            className={`font-medium ${isActiveContract ? "text-moss-primary" : "text-neutral-secondary-mid"}`}
          >
            {applyMask(total, "valuta_specific")}
          </span>
          {isConceptContract && (
            <span className="text-brick-primary text-xs">(concept)</span>
          )}
          <div className="bg-brick-primary h-2 w-2 rounded-full"></div>
        </div>
      </Tooltip>
    );
  };

  const pushPricesToOdoo = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/sync-odoo-push`,
        {
          method: "POST",
          body: JSON.stringify({ product: currentProduct }),
          headers: { "Content-Type": "application/json" },
        },
        session,
      );
      if (response.ok) {
        toast.success("Prices successfully pushed to Odoo!");

        // Refresh contracts and re-run comparison to update sync status
        await fetchRentalContracts(true);

        handleClosePriceSyncModal();
      } else {
        toast.error("Failed to push prices to Odoo.");
      }
    } catch (error) {
      toast.error("Network or server error.");
    }
  };

  const hasUpcomingPriceChanges = (contract: RentalContract) => {
    return contract.products?.some((product) =>
      product.price?.some(
        (price) => new Date(price.effectiveDate) > new Date(),
      ),
    );
  };

  const isNearIndexation = (contract: RentalContract) => {
    const today = new Date();
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(today.getMonth() + 1);

    return contract.contractMilestoneDates?.some(
      (date) =>
        date.milestoneDateType === MilestoneDateType.INDEX_DATE &&
        date.date &&
        new Date(date.date) <= oneMonthFromNow &&
        new Date(date.date) >= today,
    );
  };

  const openDeleteModal = (contract: RentalContract) => {
    if (!contract || contract.phase !== ContractPhase.CONCEPT) {
      return;
    }
    setContractToDelete(contract);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteContract = async () => {
    if (!contractToDelete) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/${contractToDelete.id}`,
        { method: "DELETE" },
        session,
      );

      if (!response.ok) {
        // Try to parse error message from response
        let errorMessage = "Er is een fout opgetreden bij het verwijderen van het contract";
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If parsing fails, use response status text
          errorMessage = `${errorMessage} (${response.status}: ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      toast.success("Contract succesvol verwijderd");
      fetchRentalContracts(); // Refresh the list
      setIsDeleteModalOpen(false);
      setContractToDelete(null);
    } catch (error) {
      console.error("Error deleting contract:", error);
      const errorMessage = error instanceof Error
        ? error.message
        : "Er is een fout opgetreden bij het verwijderen van het contract";
      toast.error(errorMessage);
    }
  };

  const handleSaveImmowebReference = async () => {
    if (!selectedUnitForReference || !immowebReferenceId) {
      toast.error("Selecteer een unit en voer een referentie-ID in");
      return;
    }

    setIsSavingReference(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/${selectedUnitForReference}/reference-id`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ referenceId: parseInt(immowebReferenceId) }),
        },
        session,
      );

      if (response.ok) {
        toast.success("Immoweb referentie succesvol opgeslagen!");
        setSelectedUnitForReference("");
        setImmowebReferenceId("");
        // Refresh both rental contracts and building data
        fetchRentalContracts(true);
        refreshBuilding?.();
      } else {
        toast.error(
          "Er is een fout opgetreden bij het opslaan van de referentie",
        );
      }
    } catch (error) {
      console.error("Error saving Immoweb reference:", error);
      toast.error(
        "Er is een fout opgetreden bij het opslaan van de referentie",
      );
    } finally {
      setIsSavingReference(false);
    }
  };

  const handleDeleteImmowebReference = async (unitId: string) => {
    if (
      !window.confirm(
        "Weet je zeker dat je deze Immoweb referentie wilt verwijderen?",
      )
    ) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/${unitId}/reference-id`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ referenceId: null }),
        },
        session,
      );

      if (response.ok) {
        toast.success("Immoweb referentie succesvol verwijderd!");
        fetchRentalContracts(true);
        refreshBuilding?.();
      } else {
        toast.error(
          "Er is een fout opgetreden bij het verwijderen van de referentie",
        );
      }
    } catch (error) {
      console.error("Error deleting Immoweb reference:", error);
      toast.error(
        "Er is een fout opgetreden bij het verwijderen van de referentie",
      );
    }
  };

  const formatTimestamp = (timestamp: string | Date) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /*
   * Open contract from the candidate list details
   */
  const location = useLocation();
  useEffect(() => {

    const allContacts = location.state?.allContacts;
    const unitId = location.state?.unitId;
    const contractType = location.state?.contractType;
    const totalResidents = location.state?.totalResidents;

    if (allContacts && allContacts.length > 0) {
      handleOpenAddContractModal(allContacts, unitId, contractType, totalResidents);

      /*
       * Remove the data from state, so after refresh it doesn' pop up instantly
       */
      window.history.replaceState({}, "");
    }


    }, [handleOpenAddContractModal, location.state]);

  

  const rentalInfo = {
    fetchRentalContracts,
    comparingOdoo,
    handleOpenPriceSyncModal,
    handleOpenViewContractModal,
    handleOpenEditProductsModal,
    handleFlagForWatchList,
    handleTerminateContract,
    handleTerminateContractAbruptly,
    hasUpcomingPriceChanges,
    isNearIndexation,
    renderBuildingAttributeRevenue,
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-14 sm:grid-cols-2">
        <div className="flex flex-col gap-6 sm:col-span-2">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-lg font-medium">
              <UsersThree weight="duotone" size={20} /> Kandidaten
            </div>
            <ButtonNew
              onClick={() => setIsCandidatesCollapsed(!isCandidatesCollapsed)}
              icon={isCandidatesCollapsed ? <CaretDown /> : <CaretUp />}
              label={
                isCandidatesCollapsed ? "Toon kandidaten" : "Verberg kandidaten"
              }
              variant="secondaryNeutral"
            />
          </header>
          {!isCandidatesCollapsed && (
            <CandidateList
              building={building}
              handleOpenAddContractModal={handleOpenAddContractModal}
            />
          )}
        </div>
        <div className="flex flex-col gap-6 sm:col-span-2">
          {/* Immoweb Reference Management */}
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-lg font-medium">
              <Globe weight="duotone" size={20} /> Immoweb referenties beheer
            </div>
            <ButtonNew
              onClick={() =>
                setIsImmowebReferencesCollapsed(!isImmowebReferencesCollapsed)
              }
              icon={isImmowebReferencesCollapsed ? <CaretDown /> : <CaretUp />}
              label={
                isImmowebReferencesCollapsed
                  ? "Toon Immoweb referenties"
                  : "Verberg Immoweb referenties"
              }
              variant="secondaryNeutral"
            />
          </header>
          {!isImmowebReferencesCollapsed && (
            <Card>
              <CardBody className="gap-4">
                {/* Existing references display */}
                {building?.units?.some(
                  (unit: UnitDTO) => unit.immowebReferenceId,
                ) && (
                  <div className="bg-neutral-primary/5 rounded-lg p-4">
                    <h4 className="text-neutral-primary mb-3 text-sm font-medium">
                      Actieve Immoweb Referenties
                    </h4>
                    <div className="space-y-2">
                      {building.units
                        .filter((unit: UnitDTO) => unit.immowebReferenceId)
                        .map((unit: UnitDTO) => (
                          <div
                            key={unit.id}
                            className="border-neutral-primary/10 flex items-center justify-between rounded-md border bg-white p-3"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-neutral-primary text-sm font-medium">
                                  Unit {unit.id}
                                </span>
                                <a
                                  href={`https://www.immoweb.be/nl/zoekertje/${unit.immowebReferenceId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-moss-primary hover:text-moss-primary/80 flex items-center gap-1 transition-colors"
                                  title="Open op Immoweb"
                                >
                                  <LinkSimple size={16} />
                                  <span className="text-sm">
                                    #{unit.immowebReferenceId}
                                  </span>
                                </a>
                              </div>
                              {unit.immowebReferenceIdAddedTimestamp && (
                                <div className="text-neutral-primary/60 mt-1 text-xs">
                                  Toegevoegd:{" "}
                                  {formatTimestamp(
                                    unit.immowebReferenceIdAddedTimestamp,
                                  )}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() =>
                                handleDeleteImmowebReference(unit.id as string)
                              }
                              className="p-1 text-red-500 transition-colors hover:text-red-600"
                              title="Verwijder referentie"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-row items-end gap-4">
                  <div className="flex-1">
                    <Select
                      label="Unit"
                      placeholder="Selecteer een unit"
                      selectedKeys={
                        selectedUnitForReference
                          ? [selectedUnitForReference]
                          : []
                      }
                      onSelectionChange={(keys) =>
                        setSelectedUnitForReference(
                          Array.from(keys)[0] as string,
                        )
                      }
                      items={building?.units?.map((unit: UnitDTO) => ({
                        key: unit.id,
                        label: `Unit ${unit.id} ${unit.immowebReferenceId ? `(huidige: #${unit.immowebReferenceId})` : ""}`,
                      }))}
                      className="max-w-xs"
                    >
                      {(unit) => (
                        <SelectItem key={unit.key}>{unit.label}</SelectItem>
                      )}
                    </Select>
                  </div>

                  <div className="flex-1">
                    <Input
                      label="Immoweb Referentie ID"
                      placeholder="Voer referentie ID in"
                      value={immowebReferenceId}
                      onChange={(e: any) =>
                        setImmowebReferenceId(e.target.value)
                      }
                      type="text"
                      className="max-w-xs"
                    />
                  </div>

                  <Button
                    color="primary"
                    className="bg-moss-primary text-white"
                    onPress={handleSaveImmowebReference}
                    isLoading={isSavingReference}
                    isDisabled={
                      !selectedUnitForReference || !immowebReferenceId
                    }
                  >
                    Opslaan
                  </Button>
                </div>

                <p className="text-neutral-secondary-mid text-sm">
                  Koppel een Immoweb referentie-ID aan een unit voor
                  automatische kandidaat uitnodigingen
                </p>
              </CardBody>
            </Card>
          )}
        </div>
        <div className="flex flex-col gap-6 sm:col-span-2">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-lg font-medium">
              <FileDashed weight="duotone" size={20} /> Contracten in concept
            </div>
            <div className="flex gap-4">
              <ButtonNew
                onClick={handleOpenAddContractModal}
                icon={<Plus />}
                label="Contract toevoegen"
                variant="primaryMoss"
              />
            </div>
          </header>
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner
                label="Gegevens aan het ophalen"
                color="success"
                size="md"
              />
            </div>
          ) : (
            <Table
              aria-label="Example static collection table"
              classNames={{
                wrapper: "shadow-none rounded-none p-0",
                thead: "h-fit border-b-2 border-neutral-primary/40",
                th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
                tbody: "border-b divide-y divide-neutral-primary/15",
                tr: "divide-x divide-neutral-primary/15",
                td: "py-2.5",
              }}
            >
              <TableHeader>
                <TableColumn>Detail</TableColumn>
                <TableColumn>Odoo</TableColumn>
                <TableColumn>Contract activeren</TableColumn>
                <TableColumn>Documentgenerator</TableColumn>
                <TableColumn className="min-w-[180px]">Unit</TableColumn>
                <TableColumn>Due diligence</TableColumn>
                <TableColumn>Huurder</TableColumn>
                <TableColumn>Fase</TableColumn>
                <TableColumn>Startdatum</TableColumn>
                <TableColumn>Juridische vorm</TableColumn>
                <TableColumn>Aantal bewoners</TableColumn>
                <TableColumn>Openstaand</TableColumn>
                <TableColumn>Geïndexeerde huurprijs (p/m)</TableColumn>
                <TableColumn>Provisies</TableColumn>
                <TableColumn>OV</TableColumn>
                <TableColumn>Gebouw attributen</TableColumn>
                <TableColumn>Facturatiefrequentie</TableColumn>
                <TableColumn>Actie</TableColumn>
              </TableHeader>
              <TableBody>
                {conceptContracts &&
                  conceptContracts.map((contract, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <span
                          onClick={() => handleOpenViewContractModal(contract)}
                          className="text-moss-primary cursor-pointer bg-transparent"
                        >
                          <Eye className="h-[20px] w-[20px]" />
                        </span>
                      </TableCell>
                      <TableCell>
                        <OdooContractAction
                          contract={contract as any}
                          onSynced={fetchRentalContracts}
                          size={20}
                        />
                      </TableCell>
                      <TableCell>
                        <span
                          onClick={() => handleActivateContract(contract)}
                          className="text-moss-primary cursor-pointer bg-transparent"
                        >
                          <Play className="h-[20px] w-[20px]" />
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <DocumentGeneratorComponent
                            incomingContract={contract as RentalContract}
                            incomingUnit={contract.unit as UnitDTO}
                            incomingDocumentCategory={DocumentCategory.CONTRACT}
                            size="sm"
                          />
                          <Tooltip content="Open document generator met vooringevulde context">
                            <Button
                              size="sm"
                              variant="flat"
                              color="primary"
                              isIconOnly
                              onPress={() => {
                                // Prepare context entities for the document generator - all inline
                                const entities: Record<string, any> = {};

                                // Add contract entity
                                if (contract) {
                                  entities.contract = {
                                    type: "contract",
                                    id: contract.id,
                                    resourceId: contract.id,
                                    name: `Contract ${contract.id}`,
                                    data: contract,
                                  };
                                }

                                // Add unit entity if available
                                if (contract.unit) {
                                  entities.unit = {
                                    type: "unit",
                                    id: contract.unit.id,
                                    resourceId: contract.unit.id,
                                    name: `Unit ${contract.unit.id}`,
                                    data: contract.unit,
                                  };
                                }

                                // Add building entity if available
                                if (contract.unit?.building) {
                                  entities.building = {
                                    type: "building",
                                    id:
                                      contract.unit.building.id ||
                                      contract.unit.building.resourceId,
                                    resourceId:
                                      contract.unit.building.resourceId ||
                                      contract.unit.building.id,
                                    name:
                                      contract.unit.building
                                        .googlePlaceLongAddress ||
                                      `Building ${contract.unit.building.id}`,
                                    data: contract.unit.building,
                                  };
                                }

                                // Add all tenant contacts - use resource instead of contact
                                const tenants =
                                  contract.contractContacts?.filter(
                                    (cc: ContractContact) =>
                                      cc.contactType === ContactType.TENANT,
                                  ) || [];

                                tenants.forEach((tenant, index) => {
                                  if (tenant?.resource) {
                                    const contactKey =
                                      index === 0
                                        ? "contact"
                                        : `contact${index + 1}`;
                                    entities[contactKey] = {
                                      type: "contact",
                                      id: tenant.resource.id,
                                      resourceId: tenant.resource.id,
                                      name: tenant.resource.name || "Contact",
                                      data: tenant.resource,
                                    };
                                  }
                                });

                                console.log(contract.contractContacts);

                                // Prepare dynamic values
                                const dynamicValues: Record<string, string> =
                                  {};

                                // Amount of tenants
                                const tenantCount =
                                  contract.contractContacts?.filter(
                                    (cc: ContractContact) =>
                                      cc.contactType === ContactType.TENANT,
                                  ).length || 0;
                                dynamicValues.amount_of_tenants =
                                  tenantCount.toString();

                                // Start date
                                const startDate =
                                  contract.contractMilestoneDates?.find(
                                    (date: ContractMilestoneDate) =>
                                      date.milestoneDateType ===
                                      MilestoneDateType.START_DATE,
                                  );
                                if (startDate?.date) {
                                  dynamicValues.start_date = formatDate(
                                    startDate.date,
                                  );
                                }

                                // End date
                                const endDate =
                                  contract.contractMilestoneDates?.find(
                                    (date: ContractMilestoneDate) =>
                                      date.milestoneDateType ===
                                      MilestoneDateType.END_DATE,
                                  );
                                if (endDate?.date) {
                                  dynamicValues.end_date = formatDate(
                                    endDate.date,
                                  );
                                }

                                // Monthly rent (active price)
                                if (contract.activePrice?.price) {
                                  dynamicValues.monthly_rent = applyMask(
                                    contract.activePrice.price,
                                    "valuta_specific",
                                  );
                                }

                                console.log(dynamicValues);

                                // Navigate to document generator with prefilled context
                                navigate("/document-generator", {
                                  state: {
                                    entities,
                                    category: "CONTRACT",
                                    dynamicValues,
                                  },
                                });
                              }}
                            >
                              <FileText size={18} weight="duotone" />
                            </Button>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <a
                            href={`/unit/${contract?.unit?.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-moss-primary hover:text-moss-primary/80 font-medium transition-colors"
                          >
                            {`${contract?.unit?.id} ${contract?.unit?.referencePlus ? `(${contract?.unit?.referencePlus})` : ""}`}
                          </a>
                          <Tooltip
                            content={
                              ("Database contract id: " + contract.id) as any
                            }
                          >
                            <span>
                              <AlertInfo className="text-neutral-secondary-mid h-[16px] w-[16px]" />
                            </span>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex cursor-pointer items-center justify-center rounded-lg">
                          <ColoredDot
                            color="green"
                            number={
                              (contract.operationalChecks &&
                                (contract.operationalChecks as any).green
                                  .count) ||
                              0
                            }
                            labels={
                              (contract.operationalChecks &&
                                (contract.operationalChecks as any).green
                                  .labels) ||
                              []
                            }
                          />
                          <ColoredDot
                            color="orange"
                            number={
                              (contract.operationalChecks &&
                                (contract.operationalChecks as any).orange
                                  .count) ||
                              0
                            }
                            labels={
                              (contract.operationalChecks &&
                                (contract.operationalChecks as any).orange
                                  .labels) ||
                              []
                            }
                          />
                          <ColoredDot
                            color="red"
                            number={
                              (contract.operationalChecks &&
                                (contract.operationalChecks as any).red
                                  .count) ||
                              0
                            }
                            labels={
                              (contract.operationalChecks &&
                                (contract.operationalChecks as any).red
                                  .labels) ||
                              []
                            }
                          />
                          <ColoredDot
                            color="transparent"
                            number={
                              (contract.operationalChecks &&
                                (contract.operationalChecks as any).transparent
                                  .count) ||
                              0
                            }
                            labels={
                              (contract.operationalChecks &&
                                (contract.operationalChecks as any).transparent
                                  .labels) ||
                              []
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Chip
                          className="gap-1"
                          startContent={
                            <User className="text-moss-primary h-3.5 w-3.5" />
                          }
                          classNames={{
                            base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                            content: "px-0 font-medium",
                          }}
                        >
                          {contract.contractContacts &&
                            contract.contractContacts
                              .filter(
                                (contact: ContractContact) =>
                                  contact.contactType == ContactType.TENANT,
                              )
                              .map((contact: any) => contact.resource?.name)
                              .join(", ")}
                        </Chip>
                      </TableCell>

                      <TableCell>{contract.phase}</TableCell>
                      <TableCell>
                        {contract.contractMilestoneDates &&
                          contract.contractMilestoneDates.filter(
                            (milestone: ContractMilestoneDate) =>
                              milestone.milestoneDateType ==
                              MilestoneDateType.START_DATE,
                          ) &&
                          formatDate(
                            new Date(
                              (contract.contractMilestoneDates as any).filter(
                                (milestone: ContractMilestoneDate) =>
                                  milestone.milestoneDateType ==
                                  MilestoneDateType.START_DATE,
                              )[0]?.date,
                            ),
                          )}
                      </TableCell>
                      <TableCell>{contract.legalForm}</TableCell>
                      <TableCell>{contract.numResidents}</TableCell>
                      <TableCell className="font-medium">
                        Aan te vullen
                      </TableCell>
                      <TableCell>
                        <RenderPrice
                          price={contract.activePrice}
                          label="Huurprijs"
                          contract={contract}
                          rentalInfo={rentalInfo}
                        />
                      </TableCell>
                      <TableCell>
                        <RenderPrice
                          price={contract.activeProvision}
                          label="Provisie"
                          contract={contract}
                          rentalInfo={rentalInfo}
                        />
                      </TableCell>
                      <TableCell>
                        <RenderPrice
                          price={contract.activePropertyTaxes}
                          label="Onroerende voorheffing"
                          contract={contract}
                          rentalInfo={rentalInfo}
                        />
                      </TableCell>
                      <TableCell>
                        {renderBuildingAttributeRevenue(contract)}
                      </TableCell>
                      <TableCell>{contract.invoicingFrequency}</TableCell>
                      <TableCell>
                        <Tooltip content="Contract verwijderen">
                          <a
                            className="text-status-bad cursor-pointer"
                            onClick={() => openDeleteModal(contract)}
                          >
                            <Trash weight="duotone" size={22} />
                          </a>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="flex flex-col gap-6 sm:col-span-2">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-lg font-medium">
              <ListBullets weight="duotone" size={20} /> Lopende contracten
            </div>
          </header>
          <div>
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner
                  label="Gegevens aan het ophalen"
                  color="success"
                  size="md"
                />
              </div>
            ) : (
              <ActiveContractsTable
                company={building?.company}
                activeContracts={activeContracts}
                rentalInfo={rentalInfo}
              />
            )}
          </div>
        </div>
        <div className="flex flex-col gap-6 sm:col-span-2">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-lg font-medium">
              <Archive weight="duotone" size={20} /> Gearchiveerde contracten
            </div>
            <ButtonNew
              onClick={handleToggleArchivedContracts}
              icon={showArchivedContracts ? <CaretUp /> : <CaretDown />}
              label={
                showArchivedContracts
                  ? "Verberg gearchiveerde contracten"
                  : "Toon gearchiveerde contracten"
              }
              variant="secondaryNeutral"
            />
          </header>
          {showArchivedContracts && (
            <div>
              {loadingArchivedContracts ? (
                <div className="flex h-full items-center justify-center">
                  <Spinner
                    label="Gearchiveerde contracten aan het ophalen"
                    color="success"
                    size="md"
                  />
                </div>
              ) : (
                <ArchivedContractsTable
                  archivedContracts={archivedContracts}
                  rentalInfo={rentalInfo}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <Modal
        isOpen={isPriceSyncModalOpen}
        onOpenChange={setIsPriceSyncModalOpen}
        isDismissable={true}
        isKeyboardDismissDisabled={true}
        className="text-neutral-primary font-normal"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <CloudSlash
                    weight="duotone"
                    size={24}
                    className="text-brick-primary"
                  />
                  <h3 className="text-lg font-medium">Synchroniseer prijzen</h3>
                </div>
                <p className="text-neutral-secondary text-sm">
                  Product: {currentProduct?.name}
                </p>
              </ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <div className="border-neutral-primary/10 rounded-lg border p-4">
                    <h4 className="mb-2 font-medium">Huidige prijzen</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-neutral-secondary text-sm">
                          Yallyhub
                        </p>
                        <p className="text-lg font-medium">
                          {applyMask(currentProduct?.price, "valuta")}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-secondary text-sm">Odoo</p>
                        <p className="text-lg font-medium">
                          {applyMask(
                            (currentProduct as any)?.odooPrice,
                            "valuta",
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <ButtonNew
                      variant="primaryMoss"
                      label={`Trek deze prijzen uit Odoo`}
                      onClick={syncPricesFromOdoo}
                    />
                    <ButtonNew
                      variant="primaryMoss"
                      onClick={pushPricesToOdoo}
                      label="Duw deze prijzen naar Odoo"
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <ButtonNew
                  variant="secondaryNeutral"
                  onClick={onClose}
                  label="Sluiten"
                />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <ModalViewContract
        fetchRentalContracts={refreshRentalContracts}
        incomingContract={selectedContract as RentalContract}
        isOpen={isViewContractModalOpen}
        onClose={handleCloseViewContractModal}
      />

      <FlagForWatchListModal
        incomingState={
          selectedContract?.flaggedForWatchlist
            ? WatchListModalState.UNFLAG
            : WatchListModalState.FLAG
        }
        incomingContract={selectedContract}
        handleContractRefresh={handleContractRefresh}
        isOpen={isFlagForWatchListModalOpen}
        onClose={handleCloseFlagForWatchListModal}
      />
      <ActivateContractModal
        incomingContract={selectedContract}
        handleContractRefresh={refreshRentalContracts}
        isOpen={isActivateContractModalOpen}
        onClose={handleCloseActivateContractModal}
      />
      <TerminateContractModal
        incomingContract={selectedContract}
        handleContractRefresh={refreshRentalContracts}
        isOpen={isTerminateContractModalOpen}
        onClose={handleCloseTerminateContractModal}
      />
      <TerminateContractAbruptModal
        incomingContract={selectedContract}
        handleContractRefresh={refreshRentalContracts}
        isOpen={isTerminateContractAbruptModalOpen}
        onClose={handleCloseTerminateContractAbruptModal}
      />
      <SetFuturePriceModal
        isOpen={isSetFuturePriceModalOpen}
        onClose={() => {
          setIsSetFuturePriceModalOpen(false);
          setSelectedProductForFuturePrice(null);
        }}
        product={selectedProductForFuturePrice}
        onSuccess={refreshRentalContracts}
      />
      <Modal
        isOpen={isDeleteModalOpen}
        onOpenChange={() => setIsDeleteModalOpen(false)}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Contract verwijderen
              </ModalHeader>
              <ModalBody>
                <p>Ben je zeker dat je dit contract wilt verwijderen?</p>
                {contractToDelete?.unit && (
                  <p className="text-neutral-secondary text-sm">
                    Contract voor unit: {contractToDelete.unit.id}
                  </p>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Ga terug
                </Button>
                <Button color="primary" onPress={handleDeleteContract}>
                  Verwijder contract
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <ModalEditContractProducts
        isOpen={isEditProductsModalOpen}
        onClose={handleCloseEditProductsModal}
        contract={selectedContractForProductEdit}
        company={building.company}
        onSuccess={refreshRentalContracts}
      />
    </>
  );
};

export default RentalTab;
